import type { PolymorphicSegment } from '@repo/poly-hooks/segment';

type SegmentLike = Pick<PolymorphicSegment, 'identify'>;

// Dedupe is a referential-equality check (`!==`) against the last-sent value.
// Restrict callers to primitives so an object/array value can't silently
// re-fire on every call (every fresh reference would compare unequal).
type PrimitiveTraitValue = string | number | boolean | null | undefined;
type PrimitiveTraits = Record<string, PrimitiveTraitValue>;

const sentTraitsBySegment = new WeakMap<SegmentLike, Map<string, PrimitiveTraitValue>>();

function getCache(segment: SegmentLike): Map<string, PrimitiveTraitValue> {
  let cache = sentTraitsBySegment.get(segment);
  if (!cache) {
    cache = new Map();
    sentTraitsBySegment.set(segment, cache);
  }
  return cache;
}

export function identifyOnce(segment: SegmentLike, traits: PrimitiveTraits): void {
  const cache = getCache(segment);
  const changed: PrimitiveTraits = {};

  for (const [key, value] of Object.entries(traits)) {
    if (!cache.has(key) || cache.get(key) !== value) {
      changed[key] = value;
      cache.set(key, value);
    }
  }

  if (Object.keys(changed).length === 0) {
    return;
  }

  segment.identify(changed);
}
